import { QueryResult } from '@apollo/client';
import { debounce } from 'lodash';
import { useEffect, useRef } from 'react';
import {
  CompanyIdOptionalInput,
  Exact,
  GetDevicesChangeSubscriptionDocument,
  GetDevicesChangeSubscriptionSubscription,
  GetVrDevicesQuery,
} from '../../../graphql/graphql-operations';

type VrDevicesQueryResult = QueryResult<
  GetVrDevicesQuery,
  Exact<{ companyIdOptionalInput: CompanyIdOptionalInput }>
>;
type eventItem = GetDevicesChangeSubscriptionSubscription['vrDevicesChanges'];

export const useBatchedVrDevicesSubscription = (
  subscribeToMore: VrDevicesQueryResult['subscribeToMore'],
  companyId: string,
  updateFn: (items: eventItem[]) => void,
  batchInterval = 100,
) => {
  const bufferRef = useRef<eventItem[]>([]);

  const applyBatch = debounce(() => {
    const bufferedItems = bufferRef.current;
    bufferRef.current = [];
    if (bufferedItems.length === 0) return;
    // You can customize how to apply the batch here
    updateFn(bufferedItems);
  }, batchInterval);

  useEffect(() => {
    const unsubscribe = subscribeToMore({
      document: GetDevicesChangeSubscriptionDocument,
      variables: {
        companyIdOptionalInput: {
          companyId: companyId,
        },
      },
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) return prev;

        const newItem = subscriptionData.data.vrDevicesChanges;
        bufferRef.current.push(newItem);
        applyBatch(); // will wait for batchInterval before applying updates
        return prev; // return unchanged now — real update handled in updateFn
      },
    });

    return () => {
      unsubscribe();
    };
  }, [subscribeToMore, companyId]);
};
